import React, { useMemo, useState } from "react";
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator, Alert } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useConvex, useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";

function toFiniteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function buildSyntheticGooglePlaceId(name, latitude, longitude) {
  const safeName = String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
  const latPart = latitude.toFixed(6);
  const lngPart = longitude.toFixed(6);
  return `housepick-ai-${safeName || "restaurant"}-${latPart}-${lngPart}`;
}

export default function HousePickDisgoRow({
  aiResult,
  googleMatch,
  disgoMatch,
  compact = false,
  onPressRestaurant,
}) {
  const convex = useConvex();
  const saveGooglePlace = useMutation(api.saveGooglePlace.saveGooglePlace);
  const [isSaving, setIsSaving] = useState(false);
  const [savedRestaurant, setSavedRestaurant] = useState(null);

  const googlePlace = googleMatch?.status === "matched" ? googleMatch.place : null;
  const fallbackAiPlace = useMemo(() => {
    const aiName = typeof aiResult?.name === "string" ? aiResult.name.trim() : "";
    const aiLat = toFiniteNumber(aiResult?.coordinates?.latitude);
    const aiLng = toFiniteNumber(aiResult?.coordinates?.longitude);
    if (!aiName) return null;
    const hasCoordinates = aiLat != null && aiLng != null;
    return {
      name: aiName,
      latitude: hasCoordinates ? aiLat : 0,
      longitude: hasCoordinates ? aiLng : 0,
      address: typeof aiResult?.address === "string" ? aiResult.address : "",
      priceLevel: undefined,
      rating: undefined,
      userRatingCount: undefined,
      googlePlaceId: hasCoordinates
        ? buildSyntheticGooglePlaceId(aiName, aiLat, aiLng)
        : `housepick-ai-${aiName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 48) || "restaurant"}-no-coords`,
      types: ["restaurant"],
      isAiFallback: true,
    };
  }, [aiResult]);
  const addSourcePlace = googlePlace || fallbackAiPlace;

  const effectiveRestaurant = useMemo(() => {
    if (savedRestaurant) return savedRestaurant;
    if (disgoMatch?.status === "matched") return disgoMatch.restaurant;
    return null;
  }, [disgoMatch, savedRestaurant]);

  if (disgoMatch?.status === "loading" || (!effectiveRestaurant && googleMatch?.status === "loading")) {
    return (
      <View style={[styles.row, compact && styles.rowCompact]}>
        <ActivityIndicator size="small" color="#9C8A80" style={styles.loader} />
        <Text style={styles.loadingText}>Checking Disgo…</Text>
      </View>
    );
  }

  if (effectiveRestaurant) {
    const restaurant = effectiveRestaurant;
    const cuisine = restaurant?.cuisine ? String(restaurant.cuisine) : "Restaurant";
    const price = restaurant?.price ? String(restaurant.price) : "";
    const rowContent = (
      <View style={[styles.row, compact && styles.rowCompact]}>
        <Ionicons name="sparkles" size={compact ? 13 : 14} color="#6B4C3B" style={styles.icon} />
        <View style={styles.textCol}>
          <Text style={styles.label}>Disgo</Text>
          <Text style={styles.title} numberOfLines={1}>
            {restaurant?.name || "Matched restaurant"}
          </Text>
          <Text style={styles.meta} numberOfLines={2}>
            {restaurant?.isPartner ? "Disgo Partner" : "Disgo Restaurant"}
            {!!cuisine && ` · ${cuisine}`}
            {!!price && ` · ${price}`}
          </Text>
        </View>
      </View>
    );
    if (typeof onPressRestaurant === "function") {
      return (
        <TouchableOpacity
          activeOpacity={0.8}
          accessibilityRole="button"
          accessibilityLabel={`Open ${restaurant?.name || "restaurant"} in map view`}
          onPress={() => onPressRestaurant(restaurant)}
        >
          {rowContent}
        </TouchableOpacity>
      );
    }
    return rowContent;
  }

  const canAdd = !!addSourcePlace && !isSaving;
  return (
    <View style={[styles.row, compact && styles.rowCompact]}>
      <Ionicons name="restaurant-outline" size={compact ? 13 : 14} color="#7D695F" style={styles.icon} />
      <View style={styles.textCol}>
        <Text style={styles.label}>Disgo</Text>
        <Text style={styles.meta}>
          {googlePlace
            ? "No matching Disgo restaurant found yet."
            : canAdd
              ? "No matching Disgo restaurant found yet. Add this AI result to Disgo."
              : "Missing restaurant name for add-to-Disgo."}
        </Text>
      </View>
      {!!addSourcePlace && (
        <TouchableOpacity
          activeOpacity={0.85}
          disabled={!canAdd}
          style={[styles.addButton, !canAdd && styles.addButtonDisabled]}
          onPress={async () => {
            if (!addSourcePlace) return;
            setIsSaving(true);
            try {
              const result = await saveGooglePlace({
                name: addSourcePlace.name,
                latitude: addSourcePlace.latitude || 0,
                longitude: addSourcePlace.longitude || 0,
                priceLevel: addSourcePlace.priceLevel,
                rating: addSourcePlace.rating,
                userRatingCount: addSourcePlace.userRatingCount,
                googlePlaceId: addSourcePlace.id || addSourcePlace.googlePlaceId,
                types: addSourcePlace.types || [],
              });
              if (!result?.restaurantId) {
                Alert.alert("Error", "Couldn't add this place to Disgo right now.");
                return;
              }
              const restaurant = await convex.query(api.getRestaurants.getRestaurantById, {
                restaurantId: result.restaurantId,
              });
              if (restaurant) {
                setSavedRestaurant(restaurant);
                onPressRestaurant?.(restaurant);
              }
            } catch {
              Alert.alert("Error", "Couldn't add this place to Disgo right now.");
            } finally {
              setIsSaving(false);
            }
          }}
        >
          {isSaving ? (
            <ActivityIndicator size="small" color="#FFFFFF" />
          ) : (
            <Text style={styles.addButtonText}>Add to disgo</Text>
          )}
        </TouchableOpacity>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    marginTop: 6,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: "#FFF7F2",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5D9D2",
  },
  rowCompact: {
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  loader: {
    marginRight: 2,
  },
  loadingText: {
    flex: 1,
    color: "#9C8A80",
    fontSize: 13,
    fontFamily: "AbhayaLibre_400Regular",
  },
  icon: {
    alignSelf: "flex-start",
    marginTop: 2,
  },
  textCol: {
    flex: 1,
    minWidth: 0,
  },
  label: {
    fontSize: 11,
    color: "#9C8A80",
    fontFamily: "AbhayaLibre_700Bold",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginBottom: 2,
  },
  title: {
    fontSize: 14,
    color: "#4d2c1d",
    fontFamily: "AbhayaLibre_700Bold",
    lineHeight: 18,
  },
  meta: {
    fontSize: 12,
    color: "#7D695F",
    fontFamily: "AbhayaLibre_400Regular",
    lineHeight: 16,
  },
  addButton: {
    backgroundColor: "#6B4C3B",
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderRadius: 999,
  },
  addButtonDisabled: {
    opacity: 0.6,
  },
  addButtonText: {
    color: "#FFFFFF",
    fontSize: 12,
    fontFamily: "AbhayaLibre_700Bold",
  },
});
